import { Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Building2, FilePenLine, LogOut, Menu, RefreshCw, Search, UserCog, X, MessageCircleQuestion } from 'lucide-react';
import { BrandLockup } from '@/components/Brand';
import { Button, LoadingBlock, useToast } from '@/components/ui';
import { useAuth } from '@/auth/AuthProvider';
import { MyAvatar } from '@/components/PersonHero';
import { Prefs } from '@/components/Prefs';
import { useT } from '@/i18n/context';
import { TextRun } from '@/i18n/TextRun';
import { visibleNavGroups } from './mobile-nav';
import { NavTrailContext } from '@/lib/nav-trail';
import { MobileTabBar } from './MobileTabBar';
import { SandboxBanner } from '@/components/SandboxBanner';
import { ViewAsBanner } from '@/components/ViewAsBanner';
import { InstallPrompt } from '@/components/InstallPrompt';
import { PushNudge } from '@/components/PushNudge';
import { QuickActions } from '@/components/QuickActions';
import { HelpMenu } from '@/components/HelpMenu';
import { openAssistant } from '@/help/assistant-store';
import { normalizeText } from '@/help/assistant';
import { setErrorReporterRole } from '@/lib/client-errors';
import { ServiceBanner } from '@/components/ServiceBanner';
import { CommandPalette } from '@/components/CommandPalette';
import { DraftsDialog } from '@/components/DraftsPanel';
import { useDraftsCount } from '@/lib/use-drafts-count';
import { MandatoryUpdateNotice, WhatsNew } from '@/components/WhatsNew';
import { latestFor, markWhatsNewSeen } from '@/help/changelog';
import { Tour } from '@/components/Tour';
import { closeTour, startTour, useTourState } from '@/help/tour-store';
import { TOUR_VERSION, tourFor } from '@/help/tours';
import { PREFS_KEY, usePreferences } from '@/lib/preferences';
import { api } from '@/lib/api';
import { homePathFor } from '@/lib/home-path';
import { usePullToRefresh, useStandalone } from '@/lib/app-mode';
import type { MyPreferences, NotificationsInbox } from '@/lib/types';
import { setAppBadge, syncPushSubscription } from '@/lib/push';
import { detectArrival, showDesktopNotification, type ArrivalState } from '@/lib/desktop-notify';

/**
 * موضعُ تمرير القائمة الجانبية (13 أكتوبر 2026 — «لما يسوّون سكرول على القائمة ويختارون شيئاً تعود القائمة للبداية»):
 * يُحفظ خارج المكوّن فيبقى إن أُعيد بناؤه، ويُعاد قبل الرسم. والسببُ الأصلي كان حدَّ التحميل الوحيد في أعلى التطبيق —
 * أولُ زيارةٍ لصفحةٍ لم تُحمَّل كانت تستبدل القشرةَ كلَّها بشاشة التحميل؛ صار للمحتوى حدُّه هنا فلا تُمسّ القائمة.
 */
let navScrollTop = 0;

export function AppShell() {
  const { me, roles, institutions, institutionId, switchInstitution, signOut, viewAs } = useAuth();
  const [open, setOpen] = useState(false);
  const [navFilter, setNavFilter] = useState('');
  const navRef = useRef<HTMLElement>(null);
  // يُعاد الموضعُ عند تركيب القشرة وحده — لا عند كل تصيير: كتابةُ scrollTop أثناء تمرير الجوال
  // بالزخم توقفه في مكانه («تشبك»)، وأيُّ تصييرٍ عابر (السحبُ للتحديث، عدّادُ التنبيهات) كان يفعلها.
  useLayoutEffect(() => { if (navRef.current) navRef.current.scrollTop = navScrollTop; }, []);
  const t = useT();
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const standalone = useStandalone();
  // عدّادُ غير المقروء لتبويب «التنبيهات» — كل دقيقة، ومن الذاكرة نفسِها التي تقرؤها صفحةُ الصندوق
  const inbox = useQuery({
    queryKey: ['me', 'notifications', 'badge'],
    queryFn: () => api.get<NotificationsInbox>('/me/notifications?unreadOnly=true&limit=1', { institutionId: null }),
    // تحت «عرضٍ بعين العضو» صندوقُ تنبيهاته شأنُه — الخادمُ يردّه والقشرةُ لا تطلبه
    enabled: !!me && !me.student && !viewAs,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  // شارةُ أيقونة التطبيق = غير المقروء (حيث تُدعم) — والخروجُ يمسحها (`signOut`)
  const unreadCount = inbox.data?.unread;
  useEffect(() => { if (unreadCount !== undefined) setAppBadge(unreadCount); }, [unreadCount]);
  // تنبيهٌ وصل والتبويبُ مفتوح (25 أكتوبر 2026): إشعارُ نظامٍ حين لا يُرى التبويب، وبلاغٌ داخل الصفحة حين يُرى
  const toast = useToast();
  const arrival = useRef<ArrivalState | undefined>(undefined);
  const inboxData = inbox.data;
  useEffect(() => {
    if (!inboxData) return;
    const { next, arrived } = detectArrival(arrival.current, inboxData);
    arrival.current = next;
    if (!arrived) return;
    if (document.visibilityState === 'visible') toast.push('info', `${arrived.titleAr} — ${arrived.bodyAr}`.slice(0, 160));
    else void showDesktopNotification(arrived);
  }, [inboxData, toast]);
  // بلاغاتُ أخطاء المتصفّح تحمل اسمَ الدور (لا الشخص) — ويُمحى عند التفكيك
  const primaryRole = roles[0] ?? null;
  useEffect(() => { setErrorReporterRole(primaryRole); return () => setErrorReporterRole(null); }, [primaryRole]);
  // بعد الدخول: إعادةُ تثبيت اشتراك الجوال إن وُجد (صفُّ الخادم قد يُمحى والجهازُ يظنّ نفسه مشتركاً)،
  // ومرّةً ثانية إن جدّد عاملُ الخدمة الاشتراكَ وهذه النافذةُ مفتوحة
  const userId = me?.user.id;
  useEffect(() => {
    if (!userId || me?.student) return;
    void syncPushSubscription().catch(() => undefined);
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const onMsg = (e: MessageEvent) => { if ((e.data as { type?: string } | null)?.type === 'push-resubscribed') void syncPushSubscription().catch(() => undefined); };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [userId, me?.student]);
  const refresh = useCallback(() => qc.invalidateQueries({ refetchType: 'active' }), [qc]);
  const ptr = usePullToRefresh(standalone, refresh);

  // الجولةُ الإرشادية (27 سبتمبر 2026): تُفتح تلقائياً **مرّةً** بعد أول دخول (بعد تغيير الكلمة
  // المؤقتة — `Protected` يسبقها)، في الصفحة الأولى، ومن الخادم نعرف أنها لم تُرَ بهذا الإصدار —
  // فلا تعود على جهازٍ جديد. وإغلاقُها بأي وجه (إنهاءٌ أو تخطٍّ) يُكتب في الخادم.
  const prefs = usePreferences();
  const tour = useTourState();
  const homePathNow = homePathFor(roles);
  const tourDue = !!me && !me.student && !!prefs.data && (prefs.data.tour.version ?? 0) < TOUR_VERSION && pathname === homePathNow;
  const autoStarted = useRef(false);
  useEffect(() => {
    if (tourDue && !autoStarted.current) { autoStarted.current = true; startTour('auto'); }
  }, [tourDue]);
  const markTour = useMutation({
    mutationFn: (outcome: 'seen' | 'skipped') => api.post<MyPreferences>('/me/preferences/tour', { version: TOUR_VERSION, outcome }, { institutionId: null }),
    onSuccess: (p) => qc.setQueryData(PREFS_KEY, p),
  });
  const ackWhatsNew = useMutation({
    mutationFn: (id: string) => api.post<MyPreferences>('/me/preferences/whats-new', { id }, { institutionId: null }),
    onSuccess: (p) => qc.setQueryData(PREFS_KEY, p),
  });
  // «?» خارج الحقول يفتح المساعد (كان مركزَ المساعدة حتى 29 سبتمبر 2026)، و`Ctrl+K`/`⌘K` من أي موضع يفتح البحث العامّ
  const [palette, setPalette] = useState(false);
  // «المسودات» (0119): زرٌّ بعدّاد في الترويسة يظهر حين يكون لديّ (أو لدى كادري) مسودات — والنافذةُ تعرض اللوحةَ نفسَها التي في /me/drafts
  const [drafts, setDrafts] = useState(false);
  const draftsCount = useDraftsCount();
  const draftsTotal = (draftsCount.data?.mine ?? 0) + (draftsCount.data?.institution ?? 0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette((v) => !v); return; }
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      e.preventDefault();
      openAssistant();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  if (!me) return null;

  // تحت «عرضٍ بعين العضو» (0122) رسائلُه خارج العرض (قرار صاحب المنصة): بنودُها تختفي من قائمته، والخادمُ يردّها أيضاً
  const groups = visibleNavGroups({ roles, studentKind: me.student?.kind ?? null })
    .map((g) => (viewAs ? { ...g, items: g.items.filter((i) => !/messages|colleagues|notifications|account/.test(i.to)) } : g))
    .filter((g) => g.items.length > 0);
  const flat = groups.flatMap((g) => g.items);
  const homePath = homePathFor(roles);
  const atHome = pathname === homePath;

  const current = institutions.find((i) => i.id === institutionId) ?? null;
  const membership = me.tenant?.membership ?? null;
  const contextLabel = current
    ? `${current.name} — ${t(`institutionType.${current.type}`)}`
    : roles.includes('REGULATORY_SUPERVISOR') ? t('shell.regulatorScope') : t('shell.platformScope');
  const roleText = roles.length ? roles.map((r) => t(`role.${r}`)).join(' · ') : t('shell.noRole');

  const navLabel = (item: (typeof groups)[number]['items'][number]) => t(item.studentLabelId && me.student ? item.studentLabelId : item.labelId);
  // مسارُ التنقّل في رأس كل صفحة يُبنى من هذه البنود نفسِها (24 أكتوبر 2026)
  const trailItems = groups.flatMap((g) => g.items.map((item) => ({ to: item.to, label: navLabel(item), group: t(g.labelId) })));
  const needle = normalizeText(navFilter);
  const shownGroups = needle
    ? groups.map((g) => ({ ...g, items: g.items.filter((item) => normalizeText(navLabel(item)).includes(needle)) })).filter((g) => g.items.length > 0)
    : groups;

  return (
    <div className="shell">
      <a className="skip-link" href="#main">تخطَّ إلى المحتوى</a>
      {open && <div className="sidebar__scrim" onClick={() => setOpen(false)} aria-hidden />}

      <aside className={`sidebar${open ? ' sidebar--open' : ''}`} data-no-pull aria-label={t('common.mainNav')}>
        <div className="sidebar__brand">
          {/* الشعارُ والاسم يفتحان صفحةَ الموقع الأساسية (طلبُ الكادر 13 أكتوبر 2026) */}
          <Link to="/?site" className="grow sidebar__home" aria-label="رفرفة — الصفحة الرئيسية للموقع" onClick={() => setOpen(false)}><BrandLockup /></Link>
          <button type="button" className="icon-btn sidebar__toggle" onClick={() => setOpen(false)} aria-label={t('common.closeMenu')}>
            <X size={18} />
          </button>
        </div>

        {/* البحثُ في القائمة (18 أكتوبر 2026 — غايتنا: «لو تضيفوا البحث من القائمة هاي بدل ما ننزل ونطلع») */}
        <div className="sidebar__search">
          <Search size={16} aria-hidden />
          <input type="search" value={navFilter} onChange={(e) => setNavFilter(e.target.value)} placeholder="ابحث في القائمة…" aria-label="ابحث في القائمة" />
          {navFilter && <button type="button" className="icon-btn" onClick={() => setNavFilter('')} aria-label="امسح البحث"><X size={14} /></button>}
        </div>

        <nav className="sidebar__nav" data-tour="nav" ref={navRef} onScroll={(e) => { navScrollTop = e.currentTarget.scrollTop; }}>
          {navFilter.trim() && shownGroups.length === 0 && <p className="caption" style={{ padding: 'var(--s-3) var(--s-4)' }}>لا بند في قائمتك بهذا الاسم.</p>}
          {shownGroups.map((g) => (
            <div key={g.labelId}>
              <p className="nav-group">{t(g.labelId)}</p>
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/app'}
                  className={({ isActive }) => `nav-item${isActive ? ' nav-item--active' : ''}`}
                  onClick={() => { setOpen(false); setNavFilter(''); }}
                  data-tour={`nav:${item.to}`}
                >
                  <item.icon aria-hidden />
                  <span className="truncate">{t(item.studentLabelId && me.student ? item.studentLabelId : item.labelId)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar__user">
          <MyAvatar />
          <div className="grow stack" style={{ minWidth: 0 }}>
            <span className="small truncate" style={{ color: 'var(--ink-900)' }}>{me.user.fullName}</span>
            <span className="caption truncate">{roleText}</span>
          </div>
          {!me.student && (
            <NavLink to="/me/account" className="icon-btn" data-tour="account" aria-label={t('nav.me.account')} title={t('nav.me.account')} onClick={() => setOpen(false)}>
              <UserCog size={17} />
            </NavLink>
          )}
          <button type="button" className="icon-btn" onClick={() => void signOut()} aria-label={t('common.signOutLong')} title={t('common.signOutLong')}>
            <LogOut size={17} />
          </button>
        </div>
        {/* أبوابُ المساعدة خرجت من «المزيد» إلى زرّ «؟» بجانب «اسأل» في الترويسة (20 أكتوبر 2026 — «مسوّية زحمة في قائمة المزيد») */}
        {/* التفضيلاتُ في أسفل الشريط — وهو نفسُه لوحُ «المزيد» على الجوال. («Powered by» للصفحات العلنية والدخول وحدها — قرار 28 سبتمبر 2026) */}
        <div className="sidebar__foot">
          <div className="sidebar__prefs"><Prefs /></div>
        </div>
      </aside>

      <div className="main">
        {/* شريط واحد يحمل السياق الحاكم: أنا في أي مؤسسة وبأي دور — كل صلاحية على الشاشة مشتقة منهما */}
        <header className="header">
          <button type="button" className="icon-btn sidebar__toggle" onClick={() => setOpen(true)} aria-label={t('common.openMenu')}>
            <Menu size={20} />
          </button>
          {/* التطبيقُ المثبَّت بلا زرّ رجوعٍ من المتصفّح (آيفون خاصةً) — فالرجوعُ هنا */}
          {standalone && !atHome && (
            <button type="button" className="icon-btn header__back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(homePath))} aria-label="رجوع">
              <ArrowRight size={20} />
            </button>
          )}
          <Link to="/?site" className="header__home" aria-label="رفرفة — الصفحة الرئيسية للموقع"><img className="header__mark" src="/brand/rafrafa-mark.webp" alt="" width={26} height={30} decoding="async" /></Link>

          <div className="context grow" data-tour="context">
            {institutions.length > 1 ? (
              <select
                className="context-select"
                value={institutionId ?? ''}
                onChange={(e) => void switchInstitution(e.target.value || null)}
                aria-label={t('shell.switchInstitution')}
              >
                <option value="">{t('shell.platformScopeOption')}</option>
                {institutions.map((i) => (
                  <option key={i.id} value={i.id}>{i.name} — {t(`role.${i.role}`)}</option>
                ))}
              </select>
            ) : (
              <span className="context-pill" title={contextLabel}>
                <Building2 size={14} aria-hidden />
                <span className="context-pill__name">{contextLabel}</span>
              </span>
            )}
            <span className="context__sep" aria-hidden>·</span>
            <span className="context__role">
              <TextRun>
                {t('shell.effectiveRole')} <strong>{roleText}</strong>
                {membership?.track ? ` · ${t(`track.${membership.track}`)}` : ''}
              </TextRun>
            </span>
          </div>

          {!me.student && draftsTotal > 0 && (
            <button type="button" className="pref-btn header__drafts" onClick={() => setDrafts(true)} aria-label={`المسودات — ${draftsTotal}`} title="المسودات"><FilePenLine aria-hidden /><b className="header__drafts-badge" aria-hidden>{draftsTotal > 99 ? '99+' : draftsTotal}</b></button>
          )}
          <button type="button" className="pref-btn header__prefs" onClick={() => setPalette(true)} aria-label="ابحث (Ctrl+K)" title="ابحث (Ctrl+K)"><Search aria-hidden /></button>
          {/* «اسأل المساعد» ظاهرٌ على الجوال والحاسوب معاً (29 سبتمبر 2026) — بابُ المساعدة الأقرب */}
          <button type="button" className="pref-btn header__ask" data-testid="header-ask" onClick={() => openAssistant()} aria-label="اسأل المساعد" title="اسأل المساعد (?)"><MessageCircleQuestion aria-hidden /><span className="header__ask-label">اسأل</span></button>
          <HelpMenu className="header__help" />
          <Prefs className="header__prefs" />
          <Button className="header__signout" variant="ghost" size="sm" icon={<LogOut size={15} />} onClick={() => void signOut()}>{t('common.signOut')}</Button>
          <NavLink to="/me/account" className="header__avatar" data-tour="account" aria-label={t('nav.me.account')}><MyAvatar size={34} /></NavLink>
        </header>

        <SandboxBanner />
        <ViewAsBanner />
        <ServiceBanner />
        {(ptr.pull > 0 || ptr.busy) && (
          <div className="ptr" style={{ blockSize: ptr.busy ? 48 : ptr.pull }} aria-live="polite">
            <RefreshCw size={18} className={ptr.busy ? 'ptr__spin' : undefined} style={{ transform: ptr.busy ? undefined : `rotate(${ptr.pull * 3}deg)` }} aria-hidden />
            <span className="caption">{ptr.busy ? 'جارٍ التحديث…' : ptr.ready ? 'أفلت للتحديث' : 'اسحب للتحديث'}</span>
          </div>
        )}
        <main id="main" tabIndex={-1} className="content">
          {/* المفتاحُ بالمسار: كلُّ صفحةٍ تدخل بحركةٍ خفيفة كما في التطبيقات (وتُلغى مع «تقليل الحركة») */}
          <div key={pathname} className="page-enter">
            {atHome && !me.student && <PushNudge />}
            {atHome && !me.student && <QuickActions />}
            {atHome && !me.student && !tourDue && !tour.open && <WhatsNew />}
            {/* حدُّ تحميلٍ للمحتوى وحده: صفحةٌ لم تُحمَّل بعد لا تستبدل القشرةَ (والقائمةَ معها) بشاشة التحميل */}
            <NavTrailContext.Provider value={trailItems}><Suspense fallback={<LoadingBlock />}><Outlet /></Suspense></NavTrailContext.Provider>
          </div>
        </main>
        <InstallPrompt />
      </div>

      {palette && !me.student && <CommandPalette onClose={() => setPalette(false)} />}
      {drafts && <DraftsDialog onClose={() => setDrafts(false)} />}
      {tour.open && (
        <Tour
          steps={tourFor(roles)}
          onDone={(outcome) => {
            closeTour(); markTour.mutate(outcome);
            const latest = latestFor(roles);
            if (!latest) return;
            markWhatsNewSeen(latest.id);
            // الجولةُ الأولى بعد أول دخول تعريفٌ بالمنصة كلِّها — فحسابُ الإدارة الجديد لا تُلاحقه بعدها نافذةُ تحديثٍ لم يعش ما قبله
            if (tour.open && tour.reason === 'auto') ackWhatsNew.mutate(latest.id);
          }}
        />
      )}
      {!me.student && !tourDue && !tour.open && !ackWhatsNew.isPending && <MandatoryUpdateNotice />}
      <MobileTabBar items={flat} homePath={homePath} unread={inbox.data?.unread ?? 0} onMore={() => setOpen((v) => !v)} moreOpen={open} />
    </div>
  );
}
